import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PaymentTypeClient, PaymentTypeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new PaymentTypeClient(API_BASE_URL, axiosInstance);

// Cached query for dropdowns
export const usePaymentTypes = () => {
    return useQuery({
        queryKey: ['paymentTypes'],
        queryFn: async () => {
            const response = await apiClient.searchPaymentTypes({
                page: 1,
                pageSize: MAX_DROPDOWN_PAGE_SIZE,
                sortBy: 'DisplayPosition',
            });
            return response?.data?.results || [];
        },
        staleTime: 4 * 60 * 60 * 1000, // 4 hours
        refetchOnReconnect: true,
    });
};

// Uncached search for admin tables
export const usePaymentTypeSearch = () => {
    const search = async (params: any) => {
        const response = await apiClient.searchPaymentTypes(params);
        return {
            results: response?.data?.results || [],
            totalCount: response?.data?.totalCount || 0,
        };
    };

    return { search };
};

// Query by ID
export const usePaymentTypeById = (id: string) => {
    return useQuery({
        queryKey: ['paymentType', id],
        queryFn: async () => {
            const response = await apiClient.getPaymentTypeById(id);
            return response?.data || null;
        },
        enabled: !!id,
    });
};

// Create mutation
export const useCreatePaymentType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: PaymentTypeDTO) => {
            return await apiClient.createPaymentType(data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['paymentTypes'] });
        },
    });
};

// Update mutation
export const useUpdatePaymentType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: PaymentTypeDTO }) => {
            return await apiClient.updatePaymentType(id, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['paymentTypes'] });
        },
    });
};

// Delete mutation
export const useDeletePaymentType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            return await apiClient.deletePaymentType(id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['paymentTypes'] });
        },
    });
};